/* eslint-disable */
/**
 * OverblikPanel.jsx — Rig forside-forside til OS-dashboard
 *
 * Erstatter akkordeon-cockpittet (OSCockpit med CockpitZone1-4).
 * Layout-sprog: IDENTISK med SagerPanel.jsx — KPI-rad, Boks-grid,
 * GrafLinje, DataTabel, TidslinjeListe, TomTilstand, StatusBadge.
 *
 * 6 paneler (top → bund):
 *   1. KPI-rad øverst       — Løn, MRR, Sager, Klienter, Beslutninger, AI-puls
 *   2. "Det der kræver mig" — beslutninger + kritiske fejl (fuldt panel, store knapper)
 *   3. To-kol: Aktivitetsgraf (30 dage) + Indbakke
 *   4. Penge-panel          — løn-status, MRR, scenarie-bjælke, Dinero-tilstand
 *   5. Vækst & synlighed    — Facebook/SEO/AEO som rigt kort-grid (aldrig en grå bjælke)
 *   6. Tidslinje            — seneste aktivitet
 *
 * Data:
 *   /api/os/cockpit         — zone1 (beslutninger), zone2 (penge), zone3 (vækst+sager), zone4 (AI)
 *   /api/os/intern/aktivitet — 30-dages aktivitetsgraf (som SagerPanel)
 *   /api/os/indbakke        — seneste ulæste poster
 *
 * Regler:
 *   - Ingen fake/hardcoded forretningstal (KPI'er viser kun verificerede data)
 *   - Løn dækket KPI: viser JA/NEJ kun hvis Dinero er live (loen_daekket != null).
 *     Ellers: "Ikke forbundet — kræver Dinero" (IKKE 60K-beløbet).
 *   - Lønforpligtelser-panel: total fra DB-sum + per-ansat tabel + tilføj/rediger/slet.
 *   - TomTilstand (med badge + ikon) hvis data mangler — ALDRIG en tom grå bjælke
 *   - Ærlige "IKKE FORBUNDET"-tilstande for Meta/SEO/Dinero
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Banknote, TrendingUp, FileText, Users, AlertCircle, Brain, CheckCircle2, Clock, Activity } from 'lucide-react';
import {
  KpiKort,
  StatusBadge,
  TomTilstand,
  DataTabel,
  TidslinjeListe,
  GrafLinje,
  SektionsHeader,
  AfventerBadge,
  ModalSkal,
  SammenklappeligSektion,
  useApiPolling,
  useWindowWidth,
  STATUS,
  TEKST,
  BG,
  KANT,
  RADIUS,
  FONT,
  SPACING,
} from '../komponenter/index';
import { beregnSparklineData } from '../ui/panelUtils';
import { Boks } from '../ui/Boks';

// ─── Konstanter ────────────────────────────────────────────────────────────────
// INGEN hardkodede forretningstal — alle tal hentes fra API.

// ─── Hjælpefunktioner ──────────────────────────────────────────────────────────

function formatDato(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('da-DK', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function formatKr(tal) {
  if (tal == null || !isFinite(tal)) return '—';
  if (Math.abs(tal) >= 1000000) return (tal / 1000000).toFixed(1).replace('.', ',') + ' M';
  if (Math.abs(tal) >= 1000)    return Math.round(tal / 1000) + ' k';
  return Math.round(tal).toLocaleString('da-DK');
}

function tidsRest(udloeberKl) {
  if (!udloeberKl) return null;
  const diff = new Date(udloeberKl) - Date.now();
  if (diff <= 0) return 'Udløbet';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h > 0) return `${h}t ${m}m`;
  return `${m}m`;
}

function formatTid(iso) {
  if (!iso) return 'Aldrig';
  const diffH = (Date.now() - new Date(iso).getTime()) / 3600000;
  if (diffH < 1) return `${Math.round(diffH * 60)} min siden`;
  if (diffH < 24) return `${Math.round(diffH)} t siden`;
  return `${Math.floor(diffH / 24)} dage siden`;
}

function prioritetFarve(p) {
  if (p === 'kritisk') return STATUS.fejl;
  if (p === 'høj')     return STATUS.opmærksomhed;
  if (p === 'medium')  return STATUS.konfigureret;
  return TEKST.sekundær;
}


// ─── BeslutningKort ────────────────────────────────────────────────────────────

function BeslutningKort({ b, onSvar, mobil = false }) {
  const [loading, setLoading]           = useState(false);
  const [lokaltSvar, setLokaltSvar]     = useState(null);
  const [kommentar, setKommentar]       = useState('');
  const [visKommentar, setVisKommentar] = useState(false);

  const rest     = tidsRest(b.udloeber_kl);
  const udloebet = rest === 'Udløbet';

  const sendSvar = useCallback(async (handling) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/os/beslutninger/${b.id}/svar`, {
        method:      'POST',
        credentials: 'include',
        headers:     { 'Content-Type': 'application/json' },
        body:        JSON.stringify({ handling, kommentar: kommentar || undefined }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert('Fejl: ' + (err.fejl || `HTTP ${res.status}`));
      } else {
        setLokaltSvar(handling);
        if (onSvar) onSvar(b.id, handling);
      }
    } catch (e) {
      alert('Netværksfejl: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [b.id, kommentar, onSvar]);

  if (lokaltSvar) {
    return (
      <div style={{
        padding:      '12px 16px',
        background:   lokaltSvar === 'GODKENDT' ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
        border:       `1px solid ${lokaltSvar === 'GODKENDT' ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`,
        borderRadius: RADIUS.kort,
        display:      'flex',
        alignItems:   'center',
        gap:          10,
      }}>
        <span style={{ fontSize: 14, color: lokaltSvar === 'GODKENDT' ? STATUS.live : STATUS.fejl }}>
          {lokaltSvar === 'GODKENDT' ? 'Godkendt' : 'Afvist'}
        </span>
        <span style={{ fontSize: 12, color: TEKST.sekundær }}>{b.titel}</span>
      </div>
    );
  }

  return (
    <div style={{
      background:   'rgba(8,12,30,0.50)',
      border:       `1px solid ${KANT.standard}`,
      borderRadius: RADIUS.kort,
      padding:      '14px 16px',
      opacity:      udloebet ? 0.5 : 1,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEKST.primær, marginBottom: 2 }}>
            {b.titel}
          </div>
          {b.opgave_titel && b.opgave_titel !== b.titel && (
            <div style={{ fontSize: 11, color: TEKST.sekundær }}>{b.opgave_titel}</div>
          )}
        </div>
        {rest && (
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px',
            borderRadius: RADIUS.pill,
            background: udloebet ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)',
            border: `1px solid ${udloebet ? 'rgba(239,68,68,0.30)' : 'rgba(245,158,11,0.30)'}`,
            color: udloebet ? STATUS.fejl : STATUS.opmærksomhed,
            whiteSpace: 'nowrap',
          }}>
            {udloebet ? 'Udløbet' : rest + ' tilbage'}
          </span>
        )}
      </div>

      {b.beskrivelse && (
        <div style={{ fontSize: 12, color: TEKST.sekundær, marginBottom: 8, lineHeight: 1.55 }}>
          {b.beskrivelse}
        </div>
      )}

      {b.konsekvens && (
        <div style={{
          fontSize: 11, color: '#94a3b8', marginBottom: 10,
          background: 'rgba(255,255,255,0.03)', borderRadius: 6,
          padding: '6px 10px', borderLeft: `2px solid ${KANT.standard}`,
        }}>
          <strong style={{ color: TEKST.sekundær }}>Konsekvens:</strong> {b.konsekvens}
        </div>
      )}

      {visKommentar && (
        <textarea
          value={kommentar}
          onChange={e => setKommentar(e.target.value)}
          placeholder="Valgfri kommentar..."
          rows={2}
          style={{
            width: '100%', boxSizing: 'border-box',
            background: 'rgba(255,255,255,0.05)', border: `1px solid ${KANT.standard}`,
            borderRadius: 6, color: TEKST.primær, fontSize: 12, padding: '6px 10px',
            resize: 'vertical', fontFamily: FONT.family, marginBottom: 10,
            outline: 'none',
          }}
        />
      )}

      {!udloebet && (
        mobil ? (
          /* ── MOBIL-KNAPPER: grid 3fr 1fr, 48px tap-targets ────────────────── */
          <div style={{ marginTop: 8 }}>
            {/* Primær-række: Godkend (bred) + Afvis (smal) */}
            <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 8, marginBottom: 6 }}>
              <button
                disabled={loading}
                onClick={() => sendSvar('GODKENDT')}
                style={{
                  background: STATUS.live, color: '#fff',
                  border: 'none', borderRadius: RADIUS.knap,
                  minHeight: 48, fontSize: 14, fontWeight: 700,
                  cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? '…' : 'Godkend'}
              </button>
              <button
                disabled={loading}
                onClick={() => sendSvar('AFVIST')}
                style={{
                  background: 'rgba(239,68,68,0.15)', color: STATUS.fejl,
                  border: `1px solid rgba(239,68,68,0.35)`, borderRadius: RADIUS.knap,
                  minHeight: 48, fontSize: 13, fontWeight: 700,
                  cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1,
                }}
              >
                Afvis
              </button>
            </div>
            {/* Sekundær-række: Kommentar-knap (diskret, fuld bredde) */}
            <button
              onClick={() => setVisKommentar(v => !v)}
              style={{
                display: 'block', width: '100%',
                background: 'none', color: TEKST.sekundær,
                border: `1px solid ${KANT.standard}`, borderRadius: RADIUS.knap,
                minHeight: 36, fontSize: 11, cursor: 'pointer',
              }}
            >
              {visKommentar ? 'Skjul kommentar' : '+ Kommentar'}
            </button>
          </div>
        ) : (
          /* ── DESKTOP-KNAPPER: flex-row (uændret) ───────────────────────────── */
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
            <button
              disabled={loading}
              onClick={() => sendSvar('GODKENDT')}
              style={{
                background: STATUS.live, color: '#fff',
                border: 'none', borderRadius: RADIUS.knap,
                padding: '8px 22px', fontSize: 13, fontWeight: 700,
                cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1,
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.opacity = '0.85'; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
            >
              Godkend
            </button>
            <button
              disabled={loading}
              onClick={() => sendSvar('AFVIST')}
              style={{
                background: 'rgba(239,68,68,0.15)', color: STATUS.fejl,
                border: `1px solid rgba(239,68,68,0.35)`, borderRadius: RADIUS.knap,
                padding: '8px 22px', fontSize: 13, fontWeight: 700,
                cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1,
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.opacity = '0.75'; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
            >
              Afvis
            </button>
            <button
              onClick={() => setVisKommentar(v => !v)}
              style={{
                background: 'none', color: TEKST.sekundær,
                border: `1px solid ${KANT.standard}`, borderRadius: RADIUS.knap,
                padding: '7px 12px', fontSize: 11, cursor: 'pointer',
              }}
            >
              {visKommentar ? 'Skjul kommentar' : '+ Kommentar'}
            </button>
            <span style={{ marginLeft: 'auto', fontSize: 10, color: TEKST.sekundær }}>
              {formatDato(b.oprettet_kl)}
            </span>
          </div>
        )
      )}
    </div>
  );
}

// ─── Indbakke-modal (genbrug fra Zone1) ───────────────────────────────────────

function IndbakkeModal({ onLuk }) {
  const [beskeder, setBeskeder] = useState([]);
  const [indlaeser, setIndlaeser] = useState(true);

  useEffect(() => {
    setIndlaeser(true);
    fetch('/api/os/indbakke?limit=50', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setBeskeder(d.beskeder || []); setIndlaeser(false); })
      .catch(() => setIndlaeser(false));
  }, []);

  const markerLaest = async (id) => {
    try {
      await fetch(`/api/os/indbakke/${id}/laest`, { method: 'PATCH', credentials: 'include' });
      setBeskeder(prev => prev.map(b => b.id === id ? { ...b, laest: true } : b));
    } catch { /* ignorér */ }
  };

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onLuk(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onLuk]);

  return (
    <div
      onClick={onLuk}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'rgba(7,13,29,0.98)',
          border: '1px solid rgba(63,212,199,0.18)',
          borderRadius: 14, width: '90%', maxWidth: 680, maxHeight: '80vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 80px rgba(0,0,0,0.7)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 22px', borderBottom: '1px solid rgba(63,212,199,0.12)',
          flexShrink: 0,
        }}>
          <div>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#e2e8f0' }}>Indbakke</span>
            <span style={{ marginLeft: 10, fontSize: 12, color: '#7a8fa6' }}>
              {beskeder.filter(b => !b.laest).length} ulæst
            </span>
          </div>
          <button onClick={onLuk} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#7a8fa6', fontSize: 20, lineHeight: 1, padding: 4,
          }}>x</button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: '12px 16px' }}>
          {indlaeser && (
            <div style={{ textAlign: 'center', color: '#7a8fa6', padding: 40, fontSize: 13 }}>
              Indlæser…
            </div>
          )}
          {!indlaeser && beskeder.length === 0 && (
            <div style={{ textAlign: 'center', color: '#7a8fa6', padding: 40, fontSize: 13 }}>
              Ingen beskeder i indbakken.
            </div>
          )}
          {beskeder.map(b => (
            <div key={b.id} style={{
              background: b.laest ? 'rgba(255,255,255,0.02)' : 'rgba(63,212,199,0.04)',
              border: `1px solid ${b.laest ? 'rgba(255,255,255,0.06)' : 'rgba(63,212,199,0.15)'}`,
              borderRadius: 8, padding: '12px 14px', marginBottom: 8,
              opacity: b.laest ? 0.65 : 1,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                {!b.laest && (
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: prioritetFarve(b.prioritet), flexShrink: 0,
                  }} />
                )}
                <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0', flex: 1 }}>
                  {b.emne}
                </span>
                <span style={{ fontSize: 10, color: '#7a8fa6', whiteSpace: 'nowrap' }}>
                  {formatDato(b.oprettet_kl)}
                </span>
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.55, marginLeft: b.laest ? 0 : 14 }}>
                {b.besked}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                <span style={{ fontSize: 10, color: '#5a6a7a' }}>fra: {b.fra_agent}</span>
                {!b.laest && (
                  <button
                    onClick={() => markerLaest(b.id)}
                    style={{
                      background: 'none', border: `1px solid rgba(63,212,199,0.25)`,
                      borderRadius: 5, color: '#3fd4c7', fontSize: 10, padding: '2px 8px',
                      cursor: 'pointer',
                    }}
                  >
                    Marker læst
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Scenarie-bjælke ──────────────────────────────────────────────────────────
// scenarier: array af { label, maal, farve } — hentes fra API (os.tilstand)

function ScenarieBjælke({ aarTotal, scenarier }) {
  if (!scenarier || scenarier.length === 0) {
    return (
      <TomTilstand
        badge="KONFIGURERET"
        ikon="kr"
        titel="Scenarie-mål ikke konfigureret"
        besked="Sæt scenarie_konservativ, scenarie_realistisk og scenarie_ambitioes i os.tilstand."
      />
    );
  }
  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      border: `1px solid ${KANT.standard}`,
      borderRadius: RADIUS.kort,
      padding: '14px 16px',
    }}>
      <div style={{
        fontSize: 11, color: TEKST.sekundær, fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 14,
      }}>
        Pace mod scenarier
      </div>
      {scenarier.map(sc => {
        const pct = aarTotal != null && sc.maal > 0
          ? Math.min(100, Math.round((aarTotal / sc.maal) * 100))
          : null;
        const erRealistisk = sc.label === 'Realistisk';
        const bjælkeFarve = pct != null && pct >= 100 ? STATUS.live
          : pct != null && pct >= 60 ? STATUS.opmærksomhed
          : sc.farve;
        return (
          <div key={sc.label} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
              <span style={{ fontSize: 12, fontWeight: erRealistisk ? 700 : 400, color: erRealistisk ? TEKST.primær : TEKST.sekundær }}>
                {sc.label}
                {erRealistisk && (
                  <span style={{
                    marginLeft: 6, fontSize: 9, fontWeight: 700, padding: '1px 6px',
                    borderRadius: 4, background: 'rgba(59,130,246,0.15)',
                    border: '1px solid rgba(59,130,246,0.3)', color: '#60a5fa',
                    textTransform: 'uppercase',
                  }}>arbejdshypotese</span>
                )}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: TEKST.dæmpet }}>{formatKr(sc.maal)} kr</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: pct != null ? bjælkeFarve : TEKST.dæmpet, minWidth: 34, textAlign: 'right' }}>
                  {pct != null ? pct + '%' : '—'}
                </span>
              </div>
            </div>
            <div style={{ height: erRealistisk ? 8 : 5, background: 'rgba(255,255,255,0.07)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                height: '100%', width: `${Math.min(100, pct || 0)}%`,
                background: bjælkeFarve, borderRadius: 4, transition: 'width .6s cubic-bezier(.4,0,.2,1)',
              }} />
            </div>
          </div>
        );
      })}
      {aarTotal == null && (
        <div style={{ fontSize: 11, color: TEKST.sekundær, marginTop: 4 }}>
          Live pace kræver Dinero-adgang — konfigurerede scenarier fra os.tilstand vises
        </div>
      )}
    </div>
  );
}

// ─── HOVED-PANEL ──────────────────────────────────────────────────────────────

export default function OverblikPanel() {
  // ── Breakpoint: mobil-FØRST (<600px) ─────────────────────────────────────────
  // isMobil styrer: 2-kol KPI-grid, beslutninger øverst, 48px tap-targets
  const isMobil = useWindowWidth() < 600;

  // ── Data-fetching ────────────────────────────────────────────────────────────
  const {
    data:      cockpit,
    loading:   cockpitLoading,
    fejl:      cockpitFejl,
    opdateret: cockpitOpdateret,
    refresh:   cockpitRefresh,
  } = useApiPolling('/os/cockpit', 60000);

  const {
    data:      aktivitet,
    loading:   aktivitetLoading,
  } = useApiPolling('/os/intern/aktivitet', 120000);

  // Første-load flag: skeleton/henter-tekst vises KUN ved første hentning.
  // Ved baggrunds-refresh forbliver eksisterende data synlige → scroll bevares.
  const aktivitetFørsteLoad = aktivitetLoading && !aktivitet;

  // ── Refresh ──────────────────────────────────────────────────────────────────
  const [refreshing, setRefreshing] = useState(false);
  const refreshAlt = useCallback(async () => {
    setRefreshing(true);
    await cockpitRefresh();
    setRefreshing(false);
  }, [cockpitRefresh]);

  // ── Indbakke-modal ───────────────────────────────────────────────────────────
  const [indbakkeModalÅben, setIndbakkeModalÅben] = useState(false);

  // ── Udled data fra cockpit ───────────────────────────────────────────────────
  const zone1 = cockpit?.zone1;
  const zone2 = cockpit?.zone2;
  const zone3 = cockpit?.zone3;
  const zone4 = cockpit?.zone4;

  const sager         = zone3?.sager;
  const penge         = zone2?.data;
  const vaekst        = zone3?.vaekst;
  const beslutninger  = zone1?.beslutninger || [];
  const afventerAntal = zone1?.afventende_antal || 0;
  const kritiske24h   = zone1?.kritiske_fejl_24h || 0;
  const indbakkeTotal = zone1?.indbakke_ulæst_total || 0;
  const indbakkePrev  = zone1?.indbakke_preview || [];

  // AI-puls
  const pulsStatus    = zone4?.puls_status;
  const senestVidenKl = zone4?.senest_viden_kl;

  // Aktivitets-sparkline
  const sparklineData = beregnSparklineData(aktivitet);

  // Aktivitets-tidslinje (seneste 12 dage)
  const aktivitetTidslinje = (() => {
    const rækker = aktivitet?.aktivitet;
    if (!Array.isArray(rækker)) return [];
    return [...rækker]
      .sort((a, b) => new Date(b.dato) - new Date(a.dato))
      .slice(0, 12)
      .map((r, i) => ({
        id:    i,
        dato:  r.dato,          // ISO-streng eller Date — new Date() håndterer begge
        titel: `${r.haendelser} handlinger`,
        tekst: `${r.aktive_sager} aktive sager den dag`,
        farve: STATUS.konfigureret,
        ikon:  <Activity size={14} />,
      }));
  })();

  // Penge-tilstand — alle tal fra API (os.tilstand). Ingen hardkodede tal.
  const erDineroLive            = zone2?.data_tilstand === 'live';
  const loenDaekket             = penge?.loen_daekket ?? null;
  const loenDaekningPct         = penge?.loen_daekning_pct ?? null;
  const loenMd                  = penge?.loenforpligtelse_md ?? null;
  const mrrLive                 = penge?.mrr ?? null;
  const aarTotal                = penge?.aar_total ?? null;
  const scenarier               = penge?.scenarier_liste ?? null;
  const loenforpligtelserListe  = penge?.loenforpligtelser_liste ?? null;

  // ── Beslutning handlet-callback ──────────────────────────────────────────────
  const handleBeslutningHandlet = useCallback(() => {
    setTimeout(cockpitRefresh, 500);
  }, [cockpitRefresh]);

  // ────────────────────────────────────────────────────────────────────────────
  return (
    // Mobil: flex-kolonne med CSS order — beslutninger løftes til toppen
    <div style={{
      fontFamily: FONT.family,
      display:        isMobil ? 'flex' : 'block',
      flexDirection:  isMobil ? 'column' : undefined,
    }}>

      {/* ── Panel-header (order: 0 — altid øverst) ──────────────────────────── */}
      <div style={{ order: 0 }}>
        <SektionsHeader
          label="Overblik"
          opdateret={cockpitOpdateret}
          onRefresh={refreshAlt}
          refreshing={refreshing}
          højreIndhold={
            afventerAntal > 0
              ? <AfventerBadge antal={afventerAntal} label="afventer godkendelse" />
              : null
          }
          style={{ marginBottom: isMobil ? 14 : 20 }}
        />

        {/* Fejl-banner */}
        {cockpitFejl && (
          <div style={{
            padding: '12px 16px', marginBottom: 14,
            background: 'rgba(239,68,68,0.10)', border: `1px solid rgba(239,68,68,0.30)`,
            borderRadius: RADIUS.kort, fontSize: 13, color: STATUS.fejl,
          }}>
            Cockpit-data utilgængelig: {cockpitFejl.message}
          </div>
        )}
      </div>

      {/* ══ 1. KPI-RAD ═══════════════════════════════════════════════════════════
            Mobil (<600px): fast 2-kol grid — 48px tryk-flader, ingen flexWrap
                            order:2 → vises EFTER beslutninger (order:1)
            Desktop: flex-wrap (identisk med SagerPanel), order ikke aktiv
          ══════════════════════════════════════════════════════════════════════ */}
      <div style={{ order: isMobil ? 2 : undefined }}>
      <div style={
        isMobil
          ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }
          : { display: 'flex', gap: SPACING.gap, flexWrap: 'wrap', marginBottom: 22 }
      }>

        {/* Løn dækket — JA/NEJ kun hvis Dinero leverer data; ellers "ikke forbundet" */}
        <KpiKort
          label="Løn dækket"
          value={
            loenDaekket === true  ? (loenDaekningPct != null ? `JA · ${loenDaekningPct}%` : 'JA') :
            loenDaekket === false ? (loenDaekningPct != null ? `NEJ · ${loenDaekningPct}%` : 'NEJ') :
            'IKKE FORBUNDET'
          }
          farve={
            loenDaekket === true  ? STATUS.live :
            loenDaekket === false ? STATUS.fejl :
            STATUS.ikkeForbundet
          }
          ikon={<Banknote size={16} />}
          badge={
            loenDaekket != null ? 'LIVE' :
            'KRÆVER DINERO'
          }
          loading={cockpitLoading && !cockpit}
          mobil={isMobil}
        />

        {/* MRR */}
        <KpiKort
          label="MRR"
          value={mrrLive != null ? formatKr(mrrLive) + ' kr' : '—'}
          farve={erDineroLive ? STATUS.live : STATUS.ikkeForbundet}
          ikon={<TrendingUp size={16} />}
          badge={erDineroLive ? 'LIVE' : 'IKKE FORBUNDET'}
          loading={cockpitLoading && !cockpit}
          mobil={isMobil}
        />

        {/* Aktive sager */}
        <KpiKort
          label="Aktive sager"
          value={sager?.aktive_sager}
          farve={STATUS.live}
          ikon={<FileText size={16} />}
          badge="LIVE"
          loading={cockpitLoading && !cockpit}
          mobil={isMobil}
        />

        {/* Nye klienter denne uge */}
        <KpiKort
          label="Nye klienter uge"
          value={sager?.nye_denne_uge}
          farve={STATUS.konfigureret}
          ikon={<Users size={16} />}
          badge="LIVE"
          delta={sager?.nye_denne_uge > 0 ? sager.nye_denne_uge : null}
          loading={cockpitLoading && !cockpit}
          mobil={isMobil}
        />

        {/* Afventende beslutninger */}
        <KpiKort
          label="Afventer beslutning"
          value={afventerAntal}
          farve={afventerAntal > 0 ? STATUS.opmærksomhed : STATUS.live}
          ikon={<AlertCircle size={16} />}
          badge={afventerAntal > 0 ? 'OPMÆRKSOMHED' : 'LIVE'}
          loading={cockpitLoading && !cockpit}
          mobil={isMobil}
        />

        {/* AI-puls */}
        <KpiKort
          label="AI-puls"
          value={
            pulsStatus === 'live'          ? formatTid(senestVidenKl) :
            pulsStatus === 'opmærksomhed'  ? 'Stille' :
            'Ingen'
          }
          farve={
            pulsStatus === 'live'         ? STATUS.ai :
            pulsStatus === 'opmærksomhed' ? STATUS.ikkeForbundet :
            STATUS.ikkeForbundet
          }
          ikon={<Brain size={16} />}
          badge={pulsStatus === 'live' ? 'AI' : 'IKKE FORBUNDET'}
          loading={cockpitLoading && !cockpit}
          mobil={isMobil}
        />
      </div>
      </div>{/* slut KPI-wrapper (order:2 på mobil) */}

      {/* ══ 2. "DET DER KRÆVER MIG" ══════════════════════════════════════════════
            Mobil: order:1 → vises FØR KPI-rækken (order:2) og resten (order:3+)
                   CEO tjekker telefon → beslutninger øverst, derefter penge/sager
            Desktop: order ikke aktiv, placering identisk med nuværende
          ══════════════════════════════════════════════════════════════════════ */}
      <Boks blur={12} padding={SPACING.kortPadding} style={{ marginBottom: isMobil ? 14 : 22, order: isMobil ? 1 : undefined }}>
        <SektionsHeader
          label="Det der kræver mig"
          højreIndhold={
            kritiske24h > 0
              ? <StatusBadge status="FEJL" tekst={`${kritiske24h} kritiske fejl`} puls />
              : afventerAntal > 0
              ? <StatusBadge status="OPMÆRKSOMHED" tekst={`${afventerAntal} afventer`} puls />
              : <StatusBadge status="LIVE" tekst="Alt roligt" />
          }
          style={{ marginBottom: 16 }}
        />

        {/* Kritiske fejl-banner */}
        {kritiske24h > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px', marginBottom: 14,
            background: 'rgba(239,68,68,0.10)', border: `1px solid rgba(239,68,68,0.30)`,
            borderRadius: RADIUS.kort,
          }}>
            <span style={{ fontSize: 14, color: STATUS.fejl, fontWeight: 700 }}>
              {kritiske24h} kritisk{kritiske24h !== 1 ? 'e' : ''} fejl
            </span>
            <span style={{ fontSize: 12, color: TEKST.sekundær }}>i de seneste 24 timer</span>
          </div>
        )}

        {/* Beslutninger */}
        {cockpitLoading && !cockpit ? (
          <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter…</div>
        ) : afventerAntal === 0 && kritiske24h === 0 ? (
          <TomTilstand
            badge="LIVE"
            ikon={<CheckCircle2 size={30} color={STATUS.live} />}
            titel="Alt roligt"
            besked="Ingen beslutninger afventer. Ingen kritiske fejl i seneste 24 timer."
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {beslutninger.map(b => (
              <BeslutningKort
                key={b.id}
                b={b}
                onSvar={handleBeslutningHandlet}
                mobil={isMobil}
              />
            ))}
          </div>
        )}
      </Boks>

      {/* ══ 3. TO-KOL: AKTIVITETSGRAF + INDBAKKE (order:3 på mobil) — sekundært ════ */}
      <SammenklappeligSektion
        titel="Aktivitet & indbakke"
        defaultÅben={false}
        isMobil={isMobil}
        style={{ marginBottom: 22, order: isMobil ? 3 : undefined }}
      >
      <div style={{ display: 'grid', gridTemplateColumns: isMobil ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18 }}>

        {/* Aktivitetsgraf — 30 dage (identisk med SagerPanel) */}
        <Boks blur={12} padding={SPACING.kortPadding}>
          <SektionsHeader label="Aktivitet — 30 dage" style={{ marginBottom: 12 }} />
          {aktivitetFørsteLoad ? (
            <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter…</div>
          ) : sparklineData ? (
            <>
              {/* OS-15: viserYAkse + viserXAkse giver brugbar kontekst for datapunkter */}
              <GrafLinje
                data={sparklineData}
                farve={STATUS.konfigureret}
                højde={120}
                fyldt={true}
                viserYAkse={true}
                viserXAkse={true}
              />
              <div style={{ marginTop: 8, fontSize: 11, color: TEKST.sekundær }}>
                Handlinger per dag — seneste 30 dage
                <StatusBadge status="LIVE" lille style={{ marginLeft: 8, verticalAlign: 'middle' }} />
              </div>
            </>
          ) : (
            <TomTilstand
              titel="Ikke nok aktivitetsdata"
              besked="Kræver min. 2 dages data fra /api/os/intern/aktivitet"
              ikon="↗"
              badge="LIVE"
            />
          )}
        </Boks>

        {/* Indbakke-panel (fuldt, ikke en liste i en akkordeon) */}
        <Boks blur={12} padding={SPACING.kortPadding}>
          <SektionsHeader
            label="Indbakke"
            højreIndhold={
              indbakkeTotal > 0
                ? <AfventerBadge antal={indbakkeTotal} label="ulæste" />
                : <StatusBadge status="LIVE" tekst="Tom" />
            }
            style={{ marginBottom: 12 }}
          />

          {cockpitLoading && !cockpit ? (
            <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter…</div>
          ) : indbakkePrev.length === 0 ? (
            <TomTilstand
              badge="LIVE"
              ikon="@"
              titel="Ingen ulæste beskeder"
              besked="Indbakken er tom — ingen agenter har skrevet til dig."
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {indbakkePrev.map(b => (
                <div key={b.id} style={{
                  background: 'rgba(63,212,199,0.04)', border: '1px solid rgba(63,212,199,0.14)',
                  borderRadius: 7, padding: '9px 12px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <span style={{
                      width: 5, height: 5, borderRadius: '50%',
                      background: prioritetFarve(b.prioritet), flexShrink: 0,
                    }} />
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: '#c8d8ee', flex: 1,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {b.emne}
                    </span>
                    <span style={{ fontSize: 9, color: TEKST.sekundær }}>{b.fra_agent}</span>
                  </div>
                  <div style={{
                    fontSize: 11, color: TEKST.sekundær, lineHeight: 1.4,
                    overflow: 'hidden', display: '-webkit-box',
                    WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', marginLeft: 11,
                  }}>
                    {b.besked}
                  </div>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={() => setIndbakkeModalÅben(true)}
            style={{
              display: 'block', width: '100%', marginTop: 12,
              background: 'none', border: '1px solid rgba(63,212,199,0.20)',
              borderRadius: RADIUS.knap, color: '#3fd4c7', fontSize: 12,
              fontWeight: 600, padding: '8px 0', cursor: 'pointer',
              textAlign: 'center', transition: 'background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(63,212,199,0.07)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
          >
            Se alle →
          </button>
          {indbakkeModalÅben && <IndbakkeModal onLuk={() => setIndbakkeModalÅben(false)} />}
        </Boks>
      </div>
      </SammenklappeligSektion>

      {/* ══ 4. PENGE-PANEL (order:4 på mobil) — sekundært ═══════════════════════ */}
      <SammenklappeligSektion
        titel="Penge & scenarier"
        defaultÅben={false}
        isMobil={isMobil}
        style={{ marginBottom: 22, order: isMobil ? 4 : undefined }}
      >
      <div style={{ display: 'grid', gridTemplateColumns: isMobil ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18 }}>

        {/* Løn dækket + MRR-kort */}
        <Boks blur={12} padding={SPACING.kortPadding}>
          <SektionsHeader
            label="Penge"
            højreIndhold={
              !erDineroLive
                ? <StatusBadge status="IKKE FORBUNDET" tekst="Dinero" lille />
                : loenDaekket === false
                ? <StatusBadge status="FEJL" tekst="LØN!" puls />
                : <StatusBadge status="LIVE" tekst="OK" lille />
            }
            style={{ marginBottom: 16 }}
          />

          {/* Løn dækket — viser JA/NEJ kun hvis Dinero er live */}
          <div style={{
            background:   loenDaekket === true  ? 'rgba(34,197,94,0.08)' :
                          loenDaekket === false ? 'rgba(239,68,68,0.10)' :
                          'rgba(71,85,105,0.10)',
            border:       `1px solid ${
                          loenDaekket === true  ? 'rgba(34,197,94,0.28)' :
                          loenDaekket === false ? 'rgba(239,68,68,0.30)' :
                          'rgba(71,85,105,0.30)'}`,
            borderTop:    `3px solid ${
                          loenDaekket === true  ? STATUS.live :
                          loenDaekket === false ? STATUS.fejl :
                          STATUS.ikkeForbundet}`,
            borderRadius: RADIUS.kort,
            padding:      '14px 16px',
            marginBottom: 12,
          }}>
            <div style={{ fontSize: 11, color: TEKST.sekundær, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
              Løn dækket — {new Date().toLocaleDateString('da-DK', { month: 'long', year: 'numeric' })}
            </div>
            <div style={{
              fontSize: 32, fontWeight: 800, lineHeight: 1,
              color: loenDaekket === true  ? STATUS.live :
                     loenDaekket === false ? STATUS.fejl :
                     STATUS.ikkeForbundet,
              marginBottom: 6,
            }}>
              {loenDaekket === true ? 'JA' : loenDaekket === false ? 'NEJ' : 'IKKE FORBUNDET'}
            </div>
            <div style={{ fontSize: 11, color: TEKST.sekundær }}>
              {loenDaekket == null
                ? <span style={{ color: TEKST.sekundær }}>Kræver Dinero-adgang for live dækningstjek</span>
                : loenMd != null
                ? <>Forpligtelse: <span style={{ color: TEKST.sekundær, fontWeight: 600 }}>{formatKr(loenMd)} kr/md</span></>
                : null
              }
              {loenDaekningPct != null && loenDaekket != null && (
                <div style={{ marginTop: 10 }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 11,
                    color: TEKST.sekundær,
                    marginBottom: 4
                  }}>
                    <span>Dækning</span>
                    <span>{loenDaekningPct}%</span>
                  </div>
                  <div style={{
                    height: 6,
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.1)',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      width: Math.min(100, loenDaekningPct) + '%',
                      borderRadius: 3,
                      background: loenDaekket ? '#0ca30c' : '#e34948',
                      transition: 'width 0.4s ease'
                    }} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* MRR + Månedsomsætning */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <KpiKort
              label="MRR"
              value={mrrLive != null ? formatKr(mrrLive) + ' kr' : '—'}
              farve={erDineroLive ? STATUS.live : STATUS.ikkeForbundet}
              badge={erDineroLive ? 'LIVE' : 'IKKE FORBUNDET'}
              style={{ flex: '1 1 130px' }}
            />
            <KpiKort
              label="Omsætning år-til-dato"
              value={aarTotal != null ? formatKr(aarTotal) + ' kr' : '—'}
              farve={erDineroLive ? STATUS.live : STATUS.ikkeForbundet}
              badge={erDineroLive ? 'LIVE' : 'IKKE FORBUNDET'}
              style={{ flex: '1 1 130px' }}
            />
          </div>

          {!erDineroLive && (
            <div style={{ marginTop: 12 }}>
              <TomTilstand
                badge="IKKE FORBUNDET"
                ikon="kr"
                titel="Dinero ikke forbundet"
                besked={zone2?.mangler || 'Kræver Dinero-adgang efter ApS-oprettelse'}
              />
            </div>
          )}
        </Boks>

        {/* Scenarie-bjælke */}
        <Boks blur={12} padding={SPACING.kortPadding}>
          <SektionsHeader label="Årsmål" style={{ marginBottom: 14 }} />
          <ScenarieBjælke aarTotal={aarTotal} scenarier={scenarier} />
        </Boks>
      </div>
      </SammenklappeligSektion>

      {/* ══ 4b. LØNFORPLIGTELSER-PANEL (order:5 på mobil) — sekundært ═════════════ */}
      <SammenklappeligSektion
        titel="Lønforpligtelser"
        defaultÅben={false}
        isMobil={isMobil}
        style={{ marginBottom: 22, order: isMobil ? 5 : undefined }}
      >
      <Boks blur={12} padding={SPACING.kortPadding}>
        <SektionsHeader
          label="Lønforpligtelser"
          højreIndhold={
            loenMd != null
              ? <StatusBadge status="KONFIGURERET" tekst={`${formatKr(loenMd)} kr/md i alt`} lille />
              : <StatusBadge status="IKKE FORBUNDET" tekst="Ingen data" lille />
          }
          style={{ marginBottom: 14 }}
        />

        {/* Total — fremhævet øverst */}
        {loenMd != null && (
          <div style={{
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'space-between',
            padding:        '10px 14px',
            marginBottom:   14,
            background:     'rgba(59,130,246,0.07)',
            border:         '1px solid rgba(59,130,246,0.20)',
            borderRadius:   RADIUS.kort,
          }}>
            <span style={{ fontSize: 13, color: TEKST.sekundær, fontWeight: 600 }}>
              Lønforpligtelser i alt (konfigureret)
            </span>
            <span style={{ fontSize: 18, fontWeight: 800, color: '#60a5fa' }}>
              {loenMd.toLocaleString('da-DK')} kr/md
            </span>
          </div>
        )}

        {/* Per-ansat tabel */}
        {loenforpligtelserListe && loenforpligtelserListe.length > 0 ? (
          <div style={{ marginBottom: 14 }}>
            {/* Header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr auto auto',
              gap: '0 12px',
              padding: '5px 10px',
              borderBottom: `1px solid ${KANT.standard}`,
            }}>
              {['Navn', 'Rolle', 'Kr/md', ''].map((h, i) => (
                <span key={i} style={{ fontSize: 10, fontWeight: 700, color: TEKST.sekundær, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</span>
              ))}
            </div>
            {/* Rækker */}
            {loenforpligtelserListe.map(p => (
              <div key={p.id} style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr auto auto',
                gap: '0 12px',
                padding: '8px 10px',
                borderBottom: `1px solid ${KANT.fin}`,
                alignItems: 'center',
                opacity: p.aktiv ? 1 : 0.45,
              }}>
                <span style={{ fontSize: 13, color: TEKST.primær, fontWeight: 500 }}>{p.navn}</span>
                <span style={{ fontSize: 12, color: TEKST.sekundær }}>{p.rolle || '—'}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: p.aktiv ? TEKST.primær : TEKST.dæmpet, textAlign: 'right' }}>
                  {p.beloeb_md != null ? p.beloeb_md.toLocaleString('da-DK') : '—'}
                </span>
                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                  {!p.aktiv && (
                    <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: 'rgba(71,85,105,0.20)', color: TEKST.dæmpet, textTransform: 'uppercase' }}>
                      inaktiv
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <TomTilstand
            badge="KONFIGURERET"
            ikon="kr"
            titel="Ingen lønforpligtelser konfigureret"
            besked="Opdateres automatisk fra Salary.dk."
          />
        )}

        <div style={{ marginTop: 8, fontSize: 10, color: TEKST.sekundær }}>
          Synkroniseres automatisk fra Salary.dk — opdateres kl. 06:30 dagligt.
        </div>
      </Boks>
      </SammenklappeligSektion>


      {/* ══ 5. VÆKST & SYNLIGHED (order:6 på mobil) — sekundært ══════════════════ */}
      <SammenklappeligSektion
        titel="Vækst & synlighed"
        defaultÅben={false}
        isMobil={isMobil}
        style={{ marginBottom: 22, order: isMobil ? 6 : undefined }}
      >
      <Boks blur={12} padding={SPACING.kortPadding}>
        <SektionsHeader
          label="Vækst & synlighed"
          højreIndhold={<StatusBadge status="IKKE FORBUNDET" tekst="Integrationer mangler" lille />}
          style={{ marginBottom: 16 }}
        />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          {/* Facebook */}
          <div style={{
            background: 'rgba(255,255,255,0.025)', border: `1px solid ${KANT.standard}`,
            borderTop: '3px solid #475569', borderRadius: RADIUS.kort, padding: '14px 16px',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: TEKST.sekundær, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Facebook-gruppe
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
              Ikke forbundet — kræver Meta API
            </div>
            <StatusBadge status="IKKE FORBUNDET" tekst="Meta API mangler" lille />
            <div style={{ fontSize: 10, color: TEKST.sekundær, marginTop: 6 }}>
              {vaekst?.facebook?.mangler || 'Kræver META_ACCESS_TOKEN'}
            </div>
          </div>

          {/* SEO */}
          <div style={{
            background: 'rgba(255,255,255,0.025)', border: `1px solid ${KANT.standard}`,
            borderTop: '3px solid #475569', borderRadius: RADIUS.kort, padding: '14px 16px',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: TEKST.sekundær, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              SEO / Organisk trafik
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#475569', marginBottom: 4 }}>—</div>
            <div style={{ fontSize: 11, color: TEKST.sekundær, marginBottom: 8 }}>ikke forbundet</div>
            <StatusBadge status="IKKE FORBUNDET" tekst="Search Console mangler" lille />
            <div style={{ fontSize: 10, color: TEKST.sekundær, marginTop: 6 }}>
              {vaekst?.seo?.mangler || 'Kræver Google Search Console API'}
            </div>
          </div>

          {/* AEO */}
          <div style={{
            background: 'rgba(255,255,255,0.025)', border: `1px solid ${KANT.standard}`,
            borderTop: '3px solid #475569', borderRadius: RADIUS.kort, padding: '14px 16px',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: TEKST.sekundær, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              AEO (AI-søgning)
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#475569', marginBottom: 4 }}>—</div>
            <div style={{ fontSize: 11, color: TEKST.sekundær, marginBottom: 8 }}>ikke forbundet</div>
            <StatusBadge status="IKKE FORBUNDET" lille />
            <div style={{ fontSize: 10, color: TEKST.sekundær, marginTop: 6 }}>
              Kræver AEO-monitoring-integration
            </div>
          </div>

          {/* Leads */}
          <div style={{
            background: 'rgba(255,255,255,0.025)', border: `1px solid ${KANT.standard}`,
            borderTop: '3px solid #475569', borderRadius: RADIUS.kort, padding: '14px 16px',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: TEKST.sekundær, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Leads (CRM)
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#475569', marginBottom: 4 }}>—</div>
            <div style={{ fontSize: 11, color: TEKST.sekundær, marginBottom: 8 }}>ikke forbundet</div>
            <StatusBadge status="IKKE FORBUNDET" lille />
            <div style={{ fontSize: 10, color: TEKST.sekundær, marginTop: 6 }}>
              Kræver CRM-integration
            </div>
          </div>
        </div>
      </Boks>
      </SammenklappeligSektion>

      {/* ══ 6. AKTIVITETS-TIDSLINJE (order:7 på mobil) — sekundært ══════════════ */}
      <SammenklappeligSektion
        titel="Aktivitetstidslinje"
        defaultÅben={false}
        isMobil={isMobil}
        style={{ order: isMobil ? 7 : undefined }}
      >
      <Boks blur={12} padding={SPACING.kortPadding}>
        <SektionsHeader label="Aktivitetsoversigt — seneste 30 dage" style={{ marginBottom: 12 }} />
        <TidslinjeListe
          items={aktivitetTidslinje}
          tomTekst="Ingen aktivitet registreret"
          loading={aktivitetFørsteLoad}
          maxHøjde={280}
        />
        <div style={{ marginTop: 10, fontSize: 11, color: TEKST.sekundær }}>
          Data: haendelser-tabel aggregeret per dag
          <StatusBadge status="LIVE" lille style={{ marginLeft: 8, verticalAlign: 'middle' }} />
        </div>
      </Boks>
      </SammenklappeligSektion>

      {/* CSS-animationer (som OSCockpit) */}
      <style>{`
        @keyframes puls-ring {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: .55; transform: scale(1.15); }
        }
      `}</style>
    </div>
  );
}
